// src/components/common/ErrorBoundary.jsx
import React, { useState } from 'react';

const API = process.env.REACT_APP_API_URL;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Actualizar estado para mostrar UI de error
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Registrar error en consola (en producción enviaríamos a servicio de logging)
    console.error('ErrorBoundary capturó un error:', error, errorInfo);

    this.setState({
      error,
      errorInfo
    });

    // Aquí podrías enviar el error a un servicio como Sentry
    // logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });

    // Recargar la página para resetear estado
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return <ErrorFallback
        error={this.state.error}
        errorInfo={this.state.errorInfo}
        onReset={this.handleReset}
      />;
    }

    return this.props.children;
  }
}

// Componente de fallback con tema adaptable
function ErrorFallback({ error, errorInfo, onReset }) {
  const [enviando, setEnviando]   = useState(false);
  const [ticketId, setTicketId]   = useState(null);
  const [ticketErr, setTicketErr] = useState('');

  const enviarTicket = async () => {
    setEnviando(true);
    setTicketErr('');
    try {
      const token = localStorage.getItem('token');
      if (!token) { setTicketErr('Debés iniciar sesión para reportar el error.'); setEnviando(false); return; }

      const descripcion = [
        `**URL:** ${window.location.href}`,
        `**Fecha:** ${new Date().toLocaleString('es-AR')}`,
        `**Navegador:** ${navigator.userAgent}`,
        '',
        `**Error:** ${error ? error.toString() : 'Desconocido'}`,
        '',
        `**Stack trace:**\n\`\`\`\n${errorInfo?.componentStack?.trim().slice(0, 1500) || 'No disponible'}\n\`\`\``,
      ].join('\n');

      const res = await fetch(`${API}/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          titulo: `Error de aplicación: ${error ? error.message?.slice(0, 100) : 'Error inesperado'}`,
          descripcion,
          prioridad: 'ALTA',
          area_id: 1,
          categoria_id: 4,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al crear el ticket');
      setTicketId(data.id || data.ticket?.id || '?');
    } catch (e) {
      setTicketErr(e.message);
    } finally {
      setEnviando(false);
    }
  };

  const colors = {
    bg: '#0a0d14',
    bgCard: '#0d1117',
    border: '#1e293b',
    shadow: '0 20px 60px rgba(0,0,0,0.5)',
    textPrimary: '#f1f5f9',
    textSecondary: '#94a3b8',
    bgInput: '#161b2e',
    textMuted: '#64748b',
    accentGradient: 'linear-gradient(135deg, #fbbf24 0%, #ea580c 100%)',
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: colors.bg,
      padding: '2rem',
    },
    card: {
      maxWidth: '600px',
      width: '100%',
      background: colors.bgCard,
      border: `1px solid ${colors.border}`,
      borderRadius: '16px',
      padding: '2rem',
      boxShadow: colors.shadow,
      textAlign: 'center',
    },
    icon: {
      fontSize: '4rem',
      marginBottom: '1rem',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '1rem',
    },
    message: {
      fontSize: '1rem',
      color: colors.textSecondary,
      marginBottom: '1.5rem',
      lineHeight: 1.6,
    },
    details: {
      background: colors.bgInput,
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
      padding: '1rem',
      marginBottom: '1.5rem',
      textAlign: 'left',
      maxHeight: '200px',
      overflowY: 'auto',
    },
    errorText: {
      fontFamily: 'monospace',
      fontSize: '0.875rem',
      color: '#ef4444',
      marginBottom: '0.5rem',
      wordBreak: 'break-word',
    },
    stackTrace: {
      fontFamily: 'monospace',
      fontSize: '0.75rem',
      color: colors.textMuted,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    },
    btnContainer: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'center',
      flexWrap: 'wrap',
    },
    btnPrimary: {
      background: colors.accentGradient,
      color: '#fff',
      border: 'none',
      borderRadius: '8px',
      padding: '0.75rem 1.5rem',
      fontSize: '1rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'transform 0.2s',
    },
    btnSecondary: {
      background: colors.bgInput,
      color: colors.textSecondary,
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
      padding: '0.75rem 1.5rem',
      fontSize: '1rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
  };

  const isDevelopment = process.env.NODE_ENV === 'development';

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.icon}>⚠️</div>

        <h1 style={styles.title}>
          Algo salió mal
        </h1>

        <p style={styles.message}>
          Lo sentimos, ocurrió un error inesperado. El equipo técnico ha sido notificado.
          Por favor, intenta recargar la página o vuelve al inicio.
        </p>

        {isDevelopment && error && (
          <div style={styles.details}>
            <div style={styles.errorText}>
              <strong>Error:</strong> {error.toString()}
            </div>
            {errorInfo && errorInfo.componentStack && (
              <div style={styles.stackTrace}>
                {errorInfo.componentStack}
              </div>
            )}
          </div>
        )}

        <div style={styles.btnContainer}>
          <button
            style={styles.btnPrimary}
            onClick={onReset}
            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            🏠 Volver al inicio
          </button>

          <button
            style={styles.btnSecondary}
            onClick={() => window.location.reload()}
            onMouseOver={(e) => { e.currentTarget.style.background = colors.border; }}
            onMouseOut={(e) => { e.currentTarget.style.background = colors.bgInput; }}
          >
            🔄 Recargar página
          </button>

          {!ticketId && (
            <button
              style={{
                ...styles.btnSecondary,
                borderColor: '#3b82f6',
                color: enviando ? colors.textMuted : '#60a5fa',
                opacity: enviando ? 0.7 : 1,
                cursor: enviando ? 'wait' : 'pointer',
              }}
              onClick={enviarTicket}
              disabled={enviando}
            >
              {enviando ? '⏳ Enviando...' : '🎫 Reportar a Sistemas'}
            </button>
          )}
        </div>

        {ticketId && (
          <div style={{
            marginTop: '1.25rem',
            background: 'rgba(34,197,94,0.1)',
            border: '1px solid rgba(34,197,94,0.3)',
            borderRadius: '8px',
            padding: '0.75rem 1rem',
            color: '#4ade80',
            fontSize: '0.9rem',
          }}>
            ✅ Ticket <strong>#{ticketId}</strong> creado y enviado a Sistemas. Te avisaremos cuando tengamos novedades.
          </div>
        )}

        {ticketErr && (
          <div style={{
            marginTop: '1rem',
            color: '#f87171',
            fontSize: '0.82rem',
          }}>
            ⚠️ {ticketErr}
          </div>
        )}

        {isDevelopment && (
          <p style={{ ...styles.message, fontSize: '0.75rem', marginTop: '1.5rem', marginBottom: 0 }}>
            💡 <em>Modo desarrollo: detalles del error visibles arriba</em>
          </p>
        )}
      </div>
    </div>
  );
}

export default ErrorBoundary;
